/**
 * kumo.auth — プレイヤーのログイン。
 *   guest()                 端末ごとの匿名プレイヤーで開始
 *   signUpWithEmail / signInWithEmail
 *   linkEmail(email, pw)    いまのゲストにメールを連携（進捗はそのまま）
 *   signOut()
 *   onChange(fn)            ログイン状態の変化
 *   signInWithProvider / linkProvider / completeRedirectSignIn   Google・Discord・Apple（authProviders.ts）。間の印は authWaiting.ts
 *   setSignInIndicator(false | fn)   ログインの間の印を消す・自分で出す（2026-10-21）
 *   sendPasswordReset / resetPassword / verifyEmail / resendVerification / pendingAction   メール（authProviders.ts）
 *   exportData() / deleteAccount({ password? })   データのエクスポート・アカウント削除（privacy）
 *   getAccessToken()        自分のサーバー（Kumo Functions など）に「誰が呼んだか」を伝えるためのトークン
 */

import type { KumoCore, KumoPlayer, KumoSession } from './core.js';
import { createIdentity, type BrowserLike, type OAuthProvider, type ProviderSignInOptions } from './authProviders.js';
import { createSignInWaiting, type SignInIndicator } from './authWaiting.js';
// privacy（exportData / deleteAccount）で使う
import { KumoError } from './core.js';
import { applyMailOptIn, mailOptInOf, type MailOptInField } from './mail.js';

/**
 * 登録・連携の任意の options（後ろに足した = 既存の引数の形を壊さない）。
 * mailOptIn: 画面の「<ゲーム名> からのお知らせをメールで受け取る」がチェックされたときだけ渡す（初期はチェックなし）。mail.ts の MailOptIn。
 * 2026-09-29 creator/mail-close: 最初の公開ではゲームのお知らせのメールを閉じたので、mailOptIn は型に出ない（mail.ts の MailOptInField）。
 *   渡されても無視する（applyMailOptIn）。中では mailOptInOf で読む（開け直したときに今までどおり動くよう、呼ぶ側のコードは残す）
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- 中身は MailOptInField（隠している間は空）
export interface EmailAuthOptions extends MailOptInField {}

export function createAuth(core: KumoCore, browser?: BrowserLike | null) {
  const identity = createIdentity(core, browser);
  // ログインの間の「待っている印」（authWaiting.ts・2026-10-21 founder「動いている間は…アニメーションが絶対に必要」）。
  // authProviders.ts の中（run）には手を入れず、外側で包む = ポップアップの止めの切り替え（core/signin-popup-fallback）と同じ所を触らない
  const waiting = createSignInWaiting(
    () => (browser === undefined ? ((globalThis as unknown as { window?: BrowserLike }).window ?? null) : browser),
    core.storageKey('oauth')
  );
  return {
    // 外部 ID・メール確認・再設定（0013 identity）。メソッドだけを展開する（getter は持たない = 展開で値が固定されない）
    ...identity,
    /** Google・Discord・Apple・X・KUMODeck でログイン。終わるまで画面の下に小さな「Signing in…」が出る（setSignInIndicator で変えられる） */
    signInWithProvider(provider: OAuthProvider, opts: ProviderSignInOptions = {}) {
      return waiting.around(provider, false, () => identity.signInWithProvider(provider, opts));
    },
    /** いまのプレイヤー（ゲスト）に Google 等を連携。印は signInWithProvider と同じ */
    linkProvider(provider: OAuthProvider, opts: ProviderSignInOptions = {}) {
      return waiting.around(provider, true, () => identity.linkProvider(provider, opts));
    },
    /** リダイレクトで戻ったページで結果を受け取る（init が自動で呼ぶ）。コードを換える間だけ印が出る */
    completeRedirectSignIn() {
      return waiting.aroundReturn(() => identity.completeRedirectSignIn());
    },
    /**
     * ログインの間の印を変える（2026-10-21）。既定 = SDK の小さな印（画面の下の真ん中・「Signing in…」/「ログイン中…」）。
     *   false … 出さない（ゲームが自分のボタンや画面で待ちを見せるとき）
     *   (state) => { … } … 自分で出す。state.busy が true の間が待ち（組み込みの印は出ない）
     *   undefined … 既定に戻す
     * 待っている間に同じボタンがもう一度押されたら、同じ結果（同じ Promise）を返す = 2 つ目の窓を開かない
     */
    setSignInIndicator(indicator: SignInIndicator | undefined) {
      waiting.setIndicator(indicator);
    },
    get player(): KumoPlayer | null {
      return core.player;
    },
    get isSignedIn() {
      return core.isSignedIn;
    },
    /**
     * いまのプレイヤーのアクセストークン（期限切れなら先にリフレッシュ）。ゲームから**自分のサーバー**を呼ぶときに
     * `Authorization: Bearer <token>` で送り、サーバー側は secret key で `POST /v1/admin/players/verify-token` に確認させる
     * （Kumo Functions のテンプレートの kumo.ts）。WHY 公開するか: 自分のサーバーのプログラムを書く自由（docs/CUSTOM-COMPUTE.md §6）に
     * 「呼び出したプレイヤーが本物か」を確かめる口が要る。トークンは 15 分で切れ、この環境のプレイヤーとしてしか通らない
     */
    async getAccessToken(): Promise<string> {
      if (!core.isSignedIn) throw new KumoError(401, 'not_signed_in', 'Sign in first (kumo.auth.guest() etc.)');
      return core.freshAccessToken();
    },
    async guest(displayName?: string): Promise<KumoPlayer> {
      const s = await core.request<KumoSession>('POST', '/v1/auth/guest', displayName ? { displayName } : {}, { auth: false });
      core.adoptSession(s);
      return s.player;
    },
    async signUpWithEmail(email: string, password: string, displayName?: string, opts: EmailAuthOptions = {}): Promise<KumoPlayer> {
      const s = await core.request<KumoSession>('POST', '/v1/auth/email/signup', { email, password, displayName }, { auth: false });
      core.adoptSession(s);
      // 登録が成功してから申し込む（登録したばかり = 確認前のアドレス → サーバーは保留にし、登録の確認メールのリンクで確定する）
      await applyMailOptIn(core, mailOptInOf(opts), email);
      return s.player;
    },
    async signInWithEmail(email: string, password: string): Promise<KumoPlayer> {
      const s = await core.request<KumoSession>('POST', '/v1/auth/email/login', { email, password }, { auth: false });
      core.adoptSession(s);
      return s.player;
    },
    async linkEmail(email: string, password: string, opts: EmailAuthOptions = {}): Promise<KumoPlayer> {
      const r = await core.withDeviceProof((proof) => core.request<{ player: KumoPlayer }>('POST', '/v1/auth/link/email', { email, password, ...proof }));
      core.updatePlayer(r.player);
      await applyMailOptIn(core, mailOptInOf(opts), email);
      return r.player;
    },
    /** いまのプレイヤーのログイン手段（メール・Google 等。ゲストは含まない） */
    async identities(): Promise<KumoPlayer['identities']> {
      return (await core.request<{ identities: KumoPlayer['identities'] }>('GET', '/v1/players/me/identities')).identities;
    },
    /**
     * ログイン手段を外す（身に覚えのない連携を消す）。この端末以外のセッションはすべてログアウトされる
     * （外した ID で入っていた別の端末＝乗っ取った人を追い出すため）。
     *
     * **最後のログイン手段は外せない**: 外した後にログインに使える手段（Google 等・パスワード付きのメール）が 1 つも残らないと、
     * サーバーが 409 を返し、`KumoError` の `code === 'last_login_method'` で失敗する（何も外れない）。
     * WHY（サーバー players/identities.ts・Quality N4 2026-09-27）: 以前は最後の 1 つも外せてゲストに戻ったが、
     * この端末のログインが切れた時点（ログアウト・端末の紛失・データの消去）で二度と入れなくなり、購入・セーブを失う。
     * 先に別の手段を付けてから（`linkEmail` / `linkProvider`）外すよう、ゲームの画面で案内する:
     * ```js
     * try { await kumo.auth.unlinkIdentity('google'); }
     * catch (e) { if (e instanceof KumoError && e.code === 'last_login_method') showHint('Add another sign-in method first'); else throw e; }
     * ```
     */
    async unlinkIdentity(provider: 'email' | OAuthProvider): Promise<KumoPlayer> {
      const r = await core.withDeviceProof((proof) => core.request<{ player: KumoPlayer }>('DELETE', `/v1/players/me/identities/${provider}`, proof));
      core.updatePlayer(r.player);
      return r.player;
    },
    async setDisplayName(displayName: string): Promise<KumoPlayer> {
      const r = await core.request<{ player: KumoPlayer }>('PATCH', '/v1/players/me', { displayName });
      core.updatePlayer(r.player);
      return r.player;
    },
    async signOut(): Promise<void> {
      const token = core.storedRefreshToken();
      if (token) await core.request('POST', '/v1/auth/logout', { refreshToken: token }, { auth: false }).catch(() => undefined);
      core.clearSession();
    },
    // ================================================================ privacy（データのエクスポート・アカウント削除）
    // サーバー側は apps/server/src/modules/privacy。ストア（App Store / Google Play）はアプリ内からのアカウント削除を
    // 要求し、GDPR はデータのコピー（持ち運び可能な形式）を求める権利を定めている。ゲームの設定画面から呼べるようにする。

    /**
     * Kumo が持っているこのプレイヤーのデータ全部（JSON）。ゲームは「ダウンロード」ボタンでファイルとして保存させればよい。
     * パスワードやトークンの hash は含まれない。
     */
    async exportData(): Promise<KumoPlayerExport> {
      return core.request<KumoPlayerExport>('GET', '/v1/players/me/export');
    },

    /**
     * アカウントを完全に削除する（取り消せない）。成功したらこの端末のセッションも消える（onChange に null が届く）。
     * 再認証が要る: メールアカウントは `{ password }` を渡す。省略時はこの端末のリフレッシュトークンで確認する
     * （ゲストはこれが唯一の鍵）。購入の記録は会計のために匿名化して残る（誰の購入かは分からなくなる）。
     */
    async deleteAccount(opts: { password?: string } = {}): Promise<void> {
      const attempt = () => {
        if (opts.password !== undefined) return core.request('DELETE', '/v1/players/me', { password: opts.password });
        const refreshToken = core.storedRefreshToken();
        if (!refreshToken) throw new KumoError(401, 'signed_out', 'Not signed in');
        return core.request('DELETE', '/v1/players/me', { refreshToken });
      };
      try {
        await attempt();
      } catch (e) {
        // アクセストークンが切れていると request() が途中でリフレッシュ（= リフレッシュトークンを回転）して再送するが、
        // 再送の本文は回転前のトークンのまま → reauthentication_failed。保存領域の新しいトークンで 1 回だけやり直す
        if (!(e instanceof KumoError) || e.code !== 'reauthentication_failed' || opts.password !== undefined) throw e;
        await attempt();
      }
      core.clearSession();
    },
    // ================================================================ /privacy

    onChange(fn: (player: KumoPlayer | null) => void) {
      return core.on('auth', fn);
    }
  };
}

/** exportData() の形（privacy）。tables はテーブル名 → 行（列名は DB のまま snake_case）。形式の版は format で判別する */
export interface KumoPlayerExport {
  format: 'kumo.player-export/v1';
  exportedAt: string;
  game: { projectId: string; slug: string; name: string; environment: 'development' | 'production' } | null;
  player: Record<string, unknown>;
  tables: Record<string, Array<Record<string, unknown>>>;
  notStored: Record<string, string>;
}

export type KumoAuth = ReturnType<typeof createAuth>;
